import { fetcher } from "@/lib/api";

import type { LibraryResponse } from "./types";
import type { PublicCourseLaunchState } from "@/features/courses/api";

export type {
  EntitlementAccessScope,
  LibraryCourse,
  LibraryResponse,
  LibraryUpgradeTarget,
} from "./types";

export type ResumeData = {
  courseId: string;
  courseSlug: string;
  courseTitle: string;
  lastLessonId: string;
  percent: number;
  isComplete: boolean;
};

export type DashboardOverviewResponse = {
  user: { _id: string; name: string; email: string; language: string };
  counts: {
    ownedCourses: number;
    ownedDigitalProducts: number;
    orders: number;
    /** B4 — number of owned courses with at least one active upgrade target. */
    upgradesAvailable?: number;
  };
  resume: ResumeData | null;
  recentOrders: Array<{
    _id: string;
    status: string;
    total: number;
    currency: string;
    createdAt: string;
  }>;
};

export type OrderItem = {
  _id: string;
  orderNumber: string;
  status: "pending" | "partially_paid" | "paid" | "cancelled" | "refunded";
  totals: { subtotal?: number; discount?: number; total: number; currency: string };
  amountPaid?: number;
  amountDue?: number;
  balanceDue?: number;
  accessDecision?: "full" | "partial";
  items: Array<{
    _id?: string;
    catalogItemId?: string;
    type: string;
    refId?: string;
    titleSnapshot: string;
    unitPrice?: number;
    currency?: string;
  }>;
  /** `LN-CRT01`: a Skill Certificate or retake order has no items, only this. */
  skillTestGrant?: { product: "certificate" | "retake"; trackSlug: string; title: string };
  createdAt: string;
};

export type OrdersResponse = { items: OrderItem[]; page: number; limit: number; total: number };

export async function fetchLibrary(token?: string) {
  const res = await fetcher<LibraryResponse>("/v1/me/library", {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
}

export async function fetchDashboardOverview(token?: string) {
  const res = await fetcher<DashboardOverviewResponse>("/v1/dashboard/overview", {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
}

export type MyStats = {
  enrolledCourses: number;
  completedCourses: number;
  inProgressCourses: number;
  lessonsCompleted: number;
  quizAttempts: number;
  certificatesEarned: number;
  recentProgress: Array<{
    courseId: string;
    title_en: string;
    title_bn: string;
    thumbnailImageId: string | null;
    slug: string;
    progressPercent: number;
    isComplete: boolean;
    lastAccessedAt: string;
    launchState: PublicCourseLaunchState;
    nextCohortStartAt: string | null;
  }>;
};

export async function getMyStats(token?: string): Promise<MyStats> {
  const res = await fetcher<MyStats>("/v1/me/stats", {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

export async function fetchMyOrders(
  opts: { page?: number; limit?: number; status?: string } = {},
  token?: string,
) {
  const params = new URLSearchParams();
  if (opts.page) params.set("page", String(opts.page));
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.status) params.set("status", opts.status);
  const q = params.toString();
  const res = await fetcher<OrdersResponse>(`/v1/me/orders${q ? `?${q}` : ""}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
}
